import * as React from 'react';

import { useConfig } from 'src/providers/ConfigProvider';
import { useConfigOption } from 'src/hooks/useConfigOption';
import { CartType } from 'src/types/Events';

import { InterstitialTypes } from 'src/types/IModal';

type ContextType = 'modal' | 'pdp' | 'cart';

export interface IFeatures {
  affirm: boolean;
  forceVisualCheckboxes: boolean;
  interstitialType: InterstitialTypes;
  invertColors: boolean;
  monthlyPrice: boolean;
  mostPopularBadge: boolean;
  prioritizeTermsPosition: boolean;
  showDeclineOption: boolean;
  showModalBrandImage: boolean;
  showPriceInButton: boolean;
  showTestimonial: boolean;
  showValuePropCta: boolean;
  showIpidDisclaimer: boolean;
  reducedDecisionModal: boolean;
  bestValueBadge: boolean;
  cartType: CartType;
}

export const FeatureContext = React.createContext<IFeatures>({
  affirm: false,
  forceVisualCheckboxes: false,
  interstitialType: InterstitialTypes.MODAL,
  invertColors: false,
  monthlyPrice: false,
  mostPopularBadge: false,
  prioritizeTermsPosition: false,
  showDeclineOption: false,
  showModalBrandImage: false,
  showPriceInButton: false,
  showTestimonial: false,
  showValuePropCta: false,
  showIpidDisclaimer: false,
  reducedDecisionModal: false,
  bestValueBadge: false,
  cartType: 'simple',
});

interface IProps {
  children: React.ReactNode;
  // NOTE: not sure if we'll use this. Probably better not to!
  context?: ContextType;
}

export const FeatureProvider = ({ children }: IProps) => {
  const config = useConfig();

  const cartType = useConfigOption('cartType', 'simple');

  // Modal features
  const affirm = React.useMemo(
    () => !!config.affirm?.showAffirm,
    [config.affirm]
  );
  const mostPopularBadge = useConfigOption('showMostPopularBadge', false);
  const monthlyPrice = React.useMemo(
    () => config.showMonthlyPrice && !affirm,
    [config.showMonthlyPrice, affirm]
  );

  // Whether to show the T&Cs link above (true) or below (false) the submit button
  const prioritizeTermsPosition = useConfigOption(
    'prioritizeTermsPosition',
    false
  );

  const showXCoverBranding = useConfigOption('showXCoverBranding', false);

  const showIpidDisclaimer = useConfigOption('showIpidDisclaimer', false);

  // Social Proof
  const showTestimonial = React.useMemo(() => {
    return ['testimonial', 'countAndTestimonial'].includes(
      config.socialProofNotification
    );
  }, [config.socialProofNotification]);

  // Decline option (PDP Radio only)
  const showDeclineOption = React.useMemo(() => {
    return config.showCtaPdpDeclineOption && config.type === 'radio';
  }, [config.showCtaPdpDeclineOption, config.type]);

  // Feature-controlled flag to show price in Add Protection so you can overwrite it via experiments
  const showPriceInButton = React.useMemo(() => !!monthlyPrice, [monthlyPrice]);

  // Value prop CTA experiment
  const showValuePropCta = React.useMemo(() => {
    return (config.experiments
      && config.experiments.length > 0
      && config.experiments.indexOf('valueprop_pdp_cta') > -1);
  }, [config.experiments]);

  const value = {
    affirm,
    forceVisualCheckboxes: false, // 2022/11/03 Exclusively used for experiments
    interstitialType: InterstitialTypes.MODAL, // 2023/01/23 slide-out-interstitial-cta experiment
    invertColors: false, // Set this to true explicitly in a FeatureOverrideProvider
    monthlyPrice,
    mostPopularBadge,
    prioritizeTermsPosition,
    showDeclineOption,
    showModalBrandImage: false, // 2023/04/10 modal-brand-image experiment
    showPriceInButton,
    showTestimonial,
    showValuePropCta,
    showXCoverBranding,
    showIpidDisclaimer,
    reducedDecisionModal: false, // 2023/01]17 Exclusively used for experiments
    bestValueBadge: false, // Set this to true explicitly in a FeatureOverrideProvider
    cartType,
  };

  return (
    <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>
  );
};

export const FeatureOverrideProvider = ({
  children,
  ...features
}: IProps & Partial<IFeatures>) => {
  const inheritedFeatures = useFeatures();
  return (
    <FeatureContext.Provider value={{ ...inheritedFeatures, ...features }}>
      {children}
    </FeatureContext.Provider>
  );
};

export const useFeatures = () => React.useContext(FeatureContext);
